// Shared constants for the portal application
import { DEFAULT_API_BASE as SHARED_API_BASE } from '../../config/constants';
import { AUTH0_CONFIG } from '../../config/auth';
import { TIMEOUTS } from '../../config/timeouts';
import { LIMITS, STORAGE_KEYS } from '../../config/limits';
import { createLogger } from '../../shared/logger.js';

const log = createLogger('portal:config');

export const DEFAULT_USER_NAME = 'Guest';
export const FALLBACK_ACCOUNT_NAME = 'LotLingo user';
export const DEFAULT_FUNCTION_HOST = SHARED_API_BASE;
export const RELATIVE_FUNCTION_BASE = '/api';
export const DEFAULT_API_BASE = DEFAULT_FUNCTION_HOST;

// Re-export commonly used constants for backward compatibility
export const ESTIMATE_CACHE_KEY = STORAGE_KEYS.ESTIMATE_CACHE;
export const CREDIT_CACHE_TTL_MS = TIMEOUTS.CREDIT_CACHE_TTL_MS;
export const BROKEN_IMAGE_SUPPRESSION_MS = TIMEOUTS.BROKEN_IMAGE_SUPPRESSION_MS;

export const AUTH0_SCOPE = AUTH0_CONFIG.SCOPE;

export const PRICE_CHECK_SPINNER_MS = TIMEOUTS.PRICE_CHECK_SPINNER_MS;
export const PENDING_PRICE_REVIEW_STORAGE = STORAGE_KEYS.PENDING_PRICE_REVIEW;

export const TABLE_COLUMN_COUNT = LIMITS.TABLE_COLUMN_COUNT;
export const COUNTDOWN_UPDATE_MS = TIMEOUTS.COUNTDOWN_UPDATE_MS;

export const BID_REFRESH_TICK_MS = TIMEOUTS.BID_REFRESH_TICK_LEGACY_MS;
export const BID_REFRESH_BATCH_LIMIT = LIMITS.BID_REFRESH_BATCH_LIMIT;

export const LOGO_REFRESH_TICK_MS = TIMEOUTS.LOGO_REFRESH_TICK_MS;
export const LOGO_REFRESH_BATCH_LIMIT = LIMITS.LOGO_REFRESH_BATCH_LIMIT;

const EMBEDDED_DEFAULT_CONFIG: Config = {
  auth0: {
    domain: AUTH0_CONFIG.DOMAIN,
    clientId: AUTH0_CONFIG.CLIENT_ID,
    audience: AUTH0_CONFIG.AUDIENCE,
  },
  auth0Domain: AUTH0_CONFIG.DOMAIN,
  auth0ClientId: AUTH0_CONFIG.CLIENT_ID,
  auth0Audience: AUTH0_CONFIG.AUDIENCE,
  apiBaseUrl: DEFAULT_API_BASE,
};

export interface Config {
  auth0: {
    domain: string;
    clientId: string;
    audience: string;
    logoutReturnTo?: string;
    allowPreviewLogout?: boolean;
    disableAuth0Logout?: boolean;
  };
  auth0Domain?: string;
  auth0ClientId?: string;
  auth0Audience?: string;
  apiBaseUrl?: string;
  superadmin?: {
    emails?: string;
    domain?: string;
  };
  realTime?: {
    scrapeStreamUrl?: string;
    scrapeUrl?: string;
    [key: string]: any;
  };
  [key: string]: any;
}

// (Global Window interface consolidated in global.d.ts to avoid redeclaration conflicts.)

function showConfigError(msg: string): void {
  if (window.__LOTLINGO_CONFIG_ERROR_SHOWN) return;
  window.__LOTLINGO_CONFIG_ERROR_SHOWN = true;
  const inject = () => {
    // Prefer early static banner if index.html created it
    if (typeof window.__revealConfigErrorBanner === 'function') {
      window.__revealConfigErrorBanner(msg);
      return;
    }
    const banner = document.createElement('div');
    banner.setAttribute('data-testid', 'config-error-banner');
    banner.className = 'config-error-banner';
    banner.textContent = '⚠️ Configuration Error: ' + msg;
    const body = document.body;
    if (!body) {
      setTimeout(inject, 50);
      return;
    }
    body.insertBefore(banner, body.firstChild);
  };
  if (!document.body) {
    document.addEventListener('DOMContentLoaded', inject, { once: true });
  } else {
    inject();
  }
}

export async function loadConfig(): Promise<Config | null> {
  let configLoaded = false;
  const configFailTimer = setTimeout(() => {
    if (!configLoaded) {
      showConfigError('Portal cannot start. (timeout)');
    }
  }, 1000);

  try {
    const response = await fetch('/config.json', {
      cache: 'no-store',
    });

    if (response.status === 404) {
      const fallback = { ...EMBEDDED_DEFAULT_CONFIG };
      log.warn('config.json missing from host; using embedded defaults.');
      window.__LOTLINGO_CONFIG__ = fallback;
      configLoaded = true;
      clearTimeout(configFailTimer);
      return fallback;
    }

    if (!response.ok) {
      throw new Error(`Config fetch failed: ${response.status}`);
    }

    const cfg: Config = await response.json();
    window.__LOTLINGO_CONFIG__ = cfg;

    if (cfg && cfg.auth0) {
      if (!cfg.auth0Domain && cfg.auth0.domain) cfg.auth0Domain = cfg.auth0.domain;
      if (!cfg.auth0ClientId && cfg.auth0.clientId) cfg.auth0ClientId = cfg.auth0.clientId;
      if (!cfg.auth0Audience && cfg.auth0.audience) cfg.auth0Audience = cfg.auth0.audience;
    }

    configLoaded = true;
    clearTimeout(configFailTimer);
    return cfg;
  } catch (error) {
    log.error('Failed to load config.json:', error);
    clearTimeout(configFailTimer);
    showConfigError('Portal cannot start. Check console for details.');
    return null;
  }
}
